import React, { useEffect, useMemo, useState } from "react";
import {
  startOfWeek,
  addMinutes,
  format,
  isValid,
  isSameDay,
  isBefore,
} from "date-fns";
import CalendarEvent from "./calendar-event";
import CalendarEventMins from "./calendar-event-mins";
import EventPopup from "./event-popup";
import { convertUTCToLocal } from "../../common/constant/constant";
import { useDispatch, useSelector } from "react-redux";
import useMediaQuery from "../../common/custom-hooks/use-media-query";
import {
  setSelectedEvent,
} from "../../../reducer/slices/calendercount/calendar-slice";
const CalendarWeekView = ({
  currentDate,
  cancelMeeting,
  calOpen,
  setCalOpen,
  setMeetId,
  closePopup,
  setTrialInfo,
  handleCall,
  callCount,
  fetchEvents,
  addToast,
}) => {
    const [currentTime, setCurrentTime] = useState(new Date());
  const { selectedEvent, allEvents, popupPosition } = useSelector(
    (state) => state.calendar,
  );
  const calendarData = useSelector((state) => state.calendar);
  const isMxDesktop = useMediaQuery("(min-width: 2560px)");
  const isSmMxDesktop = useMediaQuery("(min-width: 1920px)");
  const position = calendarData.popupPosition;
  const customStyle =
    !position && position
      ? { display: "none" }
      : {
          left:
            position.left + 450 > window.innerWidth
              ? "auto"
              : position.left + (isMxDesktop ? 50 : isSmMxDesktop ? 0 : 0),
          right: position.left + 500 > window.innerWidth ? "30px" : "auto",
          top:
            position.top + 420 > window.innerHeight
              ? "auto"
              : position.top - 300,
          bottom: position.top + 420 > window.innerHeight ? "30px" : "auto",
          height: "auto",
        };
  const dispatch = useDispatch();
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const validDate = isValid(currentDate) ? currentDate : new Date();
  const startOfCurrentWeek = startOfWeek(validDate, { weekStartsOn: 1 });

  const eventsByDayHour = useMemo(() => {
    const map = {};

    allEvents.forEach((event) => {
      const eventStart = convertUTCToLocal(event.startTime);

      const dayKey = format(eventStart.localDate, "yyyy-MM-dd");
      const hourKey = eventStart.hours;

      if (!map[dayKey]) map[dayKey] = {};
      if (!map[dayKey][hourKey]) map[dayKey][hourKey] = [];

      map[dayKey][hourKey].push({
        ...event,
        _minutes: eventStart.minutes,
      });
    });

    return map;
  }, [allEvents]);

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden relative">
      <div className="calendar-weekdays-header grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b bg-gray-100 pr-[var(--scrollbar-width)]">
        <div className="border-r border-gray-300"></div>
        {[...Array(7)].map((_, i) => {
          const day = addMinutes(startOfCurrentWeek, i * 1440);
          const isToday = isSameDay(day, new Date());
          const totalCalls = callCount.filter(x=>x.date == format(day, "yyyy-MM-dd"))?.[0]?.totalCalls || 0;
          const remainingCalls = callCount.filter(x=>x.date == format(day, "yyyy-MM-dd"))?.[0]?.remainingCalls || 0;
          return (
            <div key={i} className={`text-left p-2 ${isToday ? "blue_color active" : "title_color"}`}>
              <div>
                {isValid(day) ? (
                  <>
                    <span className={`date-big-fonts ${isToday ? "selected_date-big-fonts" : ""}`}>{format(day, "dd")}</span>
                    <br />
                    <span className={`date-small-fonts ${isToday ? "selected_date-small-fonts" : ""}`}>{format(day, "EEEE")}</span>
                  </>
                ) : (
                  "Invalid Date"
                )}
              </div>
                <div className="mt-1">
                  <div className="text-xs font-medium mb-1">
                    <span className="text-[#1B2A3D] font-[500px]">Total Count</span>
                    <span className="ml-3 bg-[#D2E2FF] text-[#4B69E4] py-[2px] px-[4px] rounded font-semibold"> {totalCalls.toString().padStart(2, "0")} </span>
                  </div>
                  <div className="text-xs font-medium mb-1">
                    <span className="text-[#1B2A3D] font-[500px]">{format(day, "yyyy-MM-dd") < format(new Date(), "yyyy-MM-dd") ? "Missed Calls" : "Remaining Calls"}</span>
                    <span className="ml-3 bg-[#FFE3DC] text-[#F27451] py-[2px] px-[4px] rounded font-semibold">{remainingCalls.toString().padStart(2, "0")} </span>
                  </div>
                </div>
            </div>
          );
        })}
      </div>

      <div className="h-[calc(100vh-289px)] overflow-y-auto">
        {[...Array(48)].map((_, i) => {
          const hour = Math.floor(i / 2);
          const minute = (i % 2) * 30;
          return (
            <div
              key={i}
              className="grid grid-cols-[80px_repeat(7,minmax(0,1fr))] border-b h-[34px] relative"
            >
              <div className="border-r border-gray-300 text-gray-500 text-sm flex items-center justify-center bg-white pt-[25px]">
                <span className="z-10">
                  {minute ? "" : `${hour}:${minute === 0 ? "00" : ""}`}
                </span>
              </div>
              {[...Array(7)].map((_, j) => {
                const day = addMinutes(
                  startOfCurrentWeek,
                  j * 1440 + hour * 60 + minute
                );
                const isToday = isSameDay(day, new Date());
                const currentHour = currentTime.getHours();
                const currentMinutes = currentTime.getMinutes();
                const todayMidnight = new Date();
                const isCurrentTimeSlot =
                  hour === currentHour &&
                  minute <= currentMinutes &&
                  currentMinutes < minute + 30;
                const isPastDay = isBefore(day, todayMidnight);
                const topPosition = currentMinutes < 60 ? "0%" : "50%";

                return (
                  <div key={j} className="border-r relative h-full">
                    {isToday && isCurrentTimeSlot && (
                      <div
                        className="absolute w-full"
                        style={{
                          top: `${(currentMinutes / 60) * 100}%`,
                          borderTop: "2px solid red",
                        }}
                      ></div>
                    )}

                    {isPastDay && isCurrentTimeSlot && (
                      <div
                        className="absolute w-full"
                        style={{
                          top: `${(currentMinutes / 60) * 100}%`,
                          borderTop: "2px dotted red",
                        }}
                      ></div>
                    )}

                    {isToday && isCurrentTimeSlot && (
                      <div
                        className={`absolute w-full h-full border-black`}
                        style={{
                          top: topPosition,
                        }}
                      ></div>
                    )}

                    <div className="flex">
                       {(() => {
                        const dayKey = format(day, "yyyy-MM-dd");
                        const hourEvents =
                          eventsByDayHour?.[dayKey]?.[hour] || [];

                        const filteredEvents = hourEvents.filter(
                          (event) =>
                            event._minutes >= minute &&
                            event._minutes < minute + 30,
                        );

                        const width = `${100 / (filteredEvents.length || 1)}%`;

                        return filteredEvents.map((event) => (
                          <CalendarEventMins
                            key={event.calendarEventID}
                            event={event}
                            style={{ width, position: "relative" }}
                          />
                        ));
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {selectedEvent && (
        <EventPopup
          setTrialInfo={setTrialInfo}
          closePopup={closePopup}
          cancelMeeting={cancelMeeting}
          onClose={() => dispatch(setSelectedEvent(null))}
          calOpen={calOpen}
          setCalOpen={setCalOpen}
          setMeetId={setMeetId}
          handleCall={handleCall}
          fetchEvents={fetchEvents}
          addToast={addToast}
          customStyle={customStyle}
        />
      )}
    </div>
  );
};

export default CalendarWeekView;
